import React from "react";
import { STRINGS } from "../../../strings";
import { globalize, translate } from "../../../translate";
import { disableScroll, enableScroll } from "../../../utils/scroll";
import { AppCard, AppCardContent } from "../../Generic/AppCard";
import { AppSlider } from "../../Generic/AppSlider";
import { AppSliderHandlerProps } from "../../Generic/AppSliderHandler";

export type MinDuration = number;
export type MaxDuration = number;

interface Props {
  minDuration: MinDuration;
  maxDuration: MaxDuration;
  onChange: (duration: [MinDuration, MaxDuration]) => void;
}

const VisitDurationAvailabilityCard = (props: Props) => {
  const handleChangeSlider = React.useCallback(
    (value: number | number[] | null | undefined) => {
      if (!Array.isArray(value) || value.length < 2) {
        throw new Error(
          `Got invalid value of handleChange in VisitDurationAvailabilityCard (${value})`
        );
      }
      props.onChange.call(null, [value[0], value[1]]);
    },
    [props.onChange]
  );

  const duration = React.useMemo(() => {
    if (props.minDuration === 0 && props.maxDuration === 24) {
      return translate(STRINGS.ANY_DURATION);
    }

    const minDuration = props.minDuration;
    const maxDuration = globalize().formatUnit(props.maxDuration, "hour", { form: "long" });

    return `${translate(STRINGS.BETWEEN)} ${minDuration} & ${maxDuration}`;
  }, [props.minDuration, props.maxDuration]);

  const title = React.useMemo(
    () => (
      <div className="__title">
        <strong>
          {translate(STRINGS.VISIT_LASTING)}: {duration}
        </strong>
      </div>
    ),
    [duration]
  );

  const sliderValue = React.useMemo(
    () => [props.minDuration, props.maxDuration],
    [props.minDuration, props.maxDuration]
  );

  const sliderStartHandler = React.useMemo(
    (): AppSliderHandlerProps => ({
      direction: "start",
      children: globalize().formatUnit(props.minDuration, "hour", { form: "short" }),
    }),
    [props.minDuration]
  );

  const sliderEndHandler = React.useMemo(
    (): AppSliderHandlerProps => ({
      direction: "end",
      children: globalize().formatUnit(props.maxDuration, "hour", { form: "short" }),
    }),
    [props.maxDuration]
  );

  const slider = React.useMemo(
    () => (
      <AppSlider
        // https://github.com/mpowaga/react-slider/issues/137
        onBeforeChange={disableScroll}
        onAfterChange={enableScroll}
        withBars={true}
        max={24}
        min={1}
        minDistance={3}
        value={sliderValue}
        step={1}
        snapDragDisabled={true}
        onChange={handleChangeSlider}
        startHandler={sliderStartHandler}
        endHandler={sliderEndHandler}
      />
    ),
    [handleChangeSlider, sliderEndHandler, sliderStartHandler, sliderValue]
  );

  return (
    <AppCard>
      <AppCardContent>
        {title}
        {slider}
      </AppCardContent>
    </AppCard>
  );
};

export default VisitDurationAvailabilityCard;
